// Shared image-upload helpers for BOTH client bundles (storefront script in
// scripts/ and the admin React app in src/). Single source of truth for the
// upload ceiling and the resize/compress pipeline. Server keeps its own copy of
// MAX_UPLOAD_BYTES in server/lib/utils.js — the two runtimes cannot share a
// module, so keep the values equal.
//
// Pipeline: a modern createImageBitmap + OffscreenCanvas path (main thread; no
// Web Worker — a BC storefront's CSP worker-src cannot be guaranteed) with a
// legacy <img>+canvas fallback for browsers without OffscreenCanvas (notably
// iOS Safari < 16.4). Every entry point ALWAYS resolves a blob/file and never
// rejects, so callers can always proceed (the server enforces the real limit).

export const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;

const isImage = (file) => !!(file && file.type && /^image\//.test(file.type));

const canUseCanvas = () =>
    typeof document !== 'undefined' &&
    typeof Image !== 'undefined' &&
    typeof URL !== 'undefined' &&
    typeof URL.createObjectURL === 'function';

const revoke = (url) => {
    if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(url);
    }
};

/** Scale (w,h) down to fit within (maxW,maxH), never scaling up. */
function fitContain(w, h, maxW, maxH) {
    const scale = Math.min(1, maxW / (w || 1), maxH / (h || 1));
    return {
        width: Math.max(1, Math.round((w || 1) * scale)),
        height: Math.max(1, Math.round((h || 1) * scale)),
    };
}

/**
 * Modern resize+compress: createImageBitmap (EXIF-oriented) + OffscreenCanvas +
 * convertToBlob('image/jpeg'). Returns a JPEG Blob under maxBytes, or null when
 * the platform lacks support (falls back) or nothing fits. NEVER rejects, and
 * always closes the ImageBitmap — even if convertToBlob throws.
 *
 * Support (caniuse): OffscreenCanvas/convertToBlob = Chrome 69+, Firefox 105+,
 * Safari/iOS 16.4+; imageOrientation 'from-image' = Chrome 112+, Firefox 111+,
 * Safari/iOS 16.0+. Every browser that reaches the OffscreenCanvas path also
 * honors imageOrientation, so portrait photos are not rotated incorrectly.
 * @param {File|Blob} file
 * @param {number} maxBytes
 * @param {{ maxDim?: number, qualities?: number[] }} [opts]
 * @returns {Promise<Blob|null>}
 */
export async function compressImageModern(file, maxBytes, { maxDim = 1600, qualities = [0.8, 0.7, 0.5, 0.4] } = {}) {
    if (!file || typeof createImageBitmap !== 'function' || typeof OffscreenCanvas === 'undefined') {
        return null;
    }

    let bmp;
    try {
        bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (e) {
        return null;
    }

    try {
        const { width, height } = fitContain(bmp.width, bmp.height, maxDim, maxDim);
        const canvas = new OffscreenCanvas(width, height);
        const ctx = canvas.getContext('2d');
        if (!ctx || typeof canvas.convertToBlob !== 'function') {
            return null;
        }
        ctx.drawImage(bmp, 0, 0, width, height);

        for (const quality of qualities) {
            const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality });
            if (blob && blob.size <= maxBytes) {
                return blob;
            }
        }
        return null;
    } catch (e) {
        return null;
    } finally {
        if (bmp && typeof bmp.close === 'function') {
            bmp.close();
        }
    }
}

/**
 * Re-encode an image blob as JPEG, lowering quality until it fits maxBytes.
 * ALWAYS resolves (never rejects/returns null): on any missing capability or
 * decode/encode failure it resolves the original blob. Legacy <img>+canvas.toBlob
 * fallback for the compress step when OffscreenCanvas is unavailable.
 * @param {Blob} blob source image blob (already resized)
 * @param {number} maxBytes target ceiling
 * @param {number[]} [qualities] descending JPEG qualities to try
 * @returns {Promise<Blob>}
 */
export function compressBlobToJpeg(blob, maxBytes, qualities = [0.7, 0.5, 0.4]) {
    return new Promise((resolve) => {
        if (!blob || !canUseCanvas()) {
            return resolve(blob);
        }

        const url = URL.createObjectURL(blob);
        const img = new Image();

        img.onload = () => {
            revoke(url);
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth || img.width;
            canvas.height = img.naturalHeight || img.height;
            const ctx = canvas.getContext && canvas.getContext('2d');

            if (!ctx || typeof canvas.toBlob !== 'function') {
                return resolve(blob);
            }

            ctx.drawImage(img, 0, 0);

            const tryQuality = (i) => {
                canvas.toBlob((out) => {
                    if (!out) {
                        return resolve(blob);
                    }
                    if (out.size <= maxBytes || i >= qualities.length - 1) {
                        return resolve(out);
                    }
                    tryQuality(i + 1);
                }, 'image/jpeg', qualities[i]);
            };

            tryQuality(0);
        };

        img.onerror = () => {
            revoke(url);
            resolve(blob);
        };

        img.src = url;
    });
}

/**
 * Legacy <img>+canvas resize+compress fallback. Falls back to the original file
 * when the DOM/canvas pipeline is unavailable or on any failure. ALWAYS resolves.
 * @param {File|Blob} file
 * @param {number} maxDim
 * @param {number} maxBytes
 * @returns {Promise<Blob|File>}
 */
function legacyResizeImage(file, maxDim, maxBytes) {
    return new Promise((resolve) => {
        if (!canUseCanvas()) {
            return resolve(file);
        }

        const url = URL.createObjectURL(file);
        const img = new Image();

        img.onload = () => {
            revoke(url);
            const { width, height } = fitContain(img.naturalWidth || img.width, img.naturalHeight || img.height, maxDim, maxDim);
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext && canvas.getContext('2d');

            if (!ctx || typeof canvas.toBlob !== 'function') {
                return resolve(file);
            }

            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob((out) => {
                if (!out) {
                    return resolve(file);
                }
                if (out.size <= maxBytes) {
                    return resolve(out);
                }
                compressBlobToJpeg(out, maxBytes).then((c) => resolve(c || out));
            }, 'image/jpeg', 0.85);
        };

        img.onerror = () => {
            revoke(url);
            resolve(file);
        };

        img.src = url;
    });
}

/**
 * Return an uploadable blob that fits the ceiling where possible. Under maxBytes
 * returns as-is, otherwise JPEG-compresses. ALWAYS resolves a blob (never
 * rejects/returns null) so the caller can always upload; the server enforces the
 * real limit as the final backstop.
 * @param {Blob} blob
 * @param {number} [maxBytes]
 * @returns {Promise<Blob>}
 */
export function finalizeUploadBlob(blob, maxBytes = MAX_UPLOAD_BYTES) {
    if (!blob || blob.size <= maxBytes) {
        return Promise.resolve(blob);
    }
    return compressBlobToJpeg(blob, maxBytes).then((out) => out || blob);
}

/**
 * Resize + compress for callers without Dropzone's built-in resize (the admin
 * app). Tries the modern OffscreenCanvas path, then the legacy <img> path, then
 * the original file. ALWAYS resolves.
 * @param {File|Blob} file
 * @param {{ maxDim?: number, maxBytes?: number }} [opts]
 * @returns {Promise<Blob|File>}
 */
export async function resizeAndCompressImage(file, { maxDim = 1600, maxBytes = MAX_UPLOAD_BYTES } = {}) {
    if (!isImage(file)) {
        return file;
    }
    const modern = await compressImageModern(file, maxBytes, { maxDim });
    if (modern) {
        return modern;
    }
    return legacyResizeImage(file, maxDim, maxBytes);
}

/**
 * Dropzone transformFile logic, decoupled for testing. Tries the modern path,
 * then the caller-provided (Dropzone, EXIF-aware) resize + finalizeUploadBlob.
 * Calls done() EXACTLY ONCE on EVERY path so the upload queue never stalls.
 * @param {File} file
 * @param {{ resizeImage: (file: File, cb: (blob: Blob) => void) => void, maxBytes?: number, maxDim?: number }} deps
 * @param {(blob: Blob|File) => void} done
 */
export function transformForUpload(file, { resizeImage, maxBytes = MAX_UPLOAD_BYTES, maxDim = 1600 }, done) {
    if (!isImage(file)) {
        return done(file);
    }
    compressImageModern(file, maxBytes, { maxDim }).then((modern) => {
        if (modern) {
            return done(modern);
        }
        resizeImage(file, (resized) => {
            finalizeUploadBlob(resized || file, maxBytes).then((out) => done(out || resized || file));
        });
    });
}
